// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Which arcade tiles run attract, and what that costs (G1-12; FR-024, 08 §2, NFR-023).
//
// 08 §2 asks for a roster where each tile is "running live attract". The obvious implementation is
// one renderer per tile, and it is the one that cannot ship: a browser caps live WebGL contexts at
// around sixteen and **evicts the oldest without asking**, so a roster of twenty would take the
// front door's own machine down with it — the visitor's game stops because they scrolled a list.
//
// So the decisions are here, as a pure function, and the drawing is somewhere else:
//
//   * **One renderer, shared.** `TileAttract` paints from a single offscreen surface and blits into
//     each tile. The cost of the roster is the cost of the cap, not the cost of the roster, which is
//     the property `test/client/attract-tiles.test.ts` measures rather than asserts.
//   * **Only what is on screen, and only a few of those.** A tile below the fold is a tile nobody
//     is looking at.
//   * **Only a tile whose picture is in hand or can be got cheaply.** A roster row carries a name,
//     an era and a hash, and there is nothing in it to animate. This used to end the matter, on the
//     grounds that "fetching sixty kilobytes per tile would make browsing the most expensive thing
//     in the product" — and that figure was the **uncompressed** definition. Measured over the wire
//     the two shipped machines are 7.6 and 9.7 KB gzipped, and the four fields a thumbnail actually
//     draws are 1.3 and 1.6. So M8-79 added `GET .../art`, which answers those four fields, is
//     public where the definition route is gated, and is cached content-addressed in the browser
//     and immutably by HTTP. `tilesToFetch` below decides who asks.
//   * **Nothing at all under `prefers-reduced-motion`** (NFR-023, 06 §6). Not slowed: off. A grid of
//     pulsing lamps is exactly the surface that setting exists for.
//
// **Say which numbers are judgement.** `MAX_LIVE_TILES` is a choice, not a measurement, and it says
// so where it is declared. There is no roster with twenty machines on it yet to measure against;
// what *is* measured is the shape of the cost, which is the part that has to be true before one
// exists.

/** One tile, as much of it as the decision needs. */
export interface AttractCandidate {
  readonly slug: string
  /** Whether this tile's art is in hand. A roster row alone is not enough to draw. */
  readonly hasDefinition: boolean
  /** Whether the tile is inside the viewport, from an `IntersectionObserver`. */
  readonly onScreen: boolean
  /**
   * Whether art could be got for this tile at all (M8-79).
   *
   * The bundled flagship carries its own and never asks. Any published machine can be asked about,
   * because the art route is ungated. What cannot is a tile with no version to name — a roster row
   * that arrived without a semver, which `machineFeed` narrows away rather than invents.
   *
   * **Required rather than optional**, deliberately: an optional flag defaults to `false` at every
   * call site that has not heard of it, and a feature that is off everywhere while every test
   * passes is this repository's most-recorded failure.
   */
  readonly fetchable: boolean
}

export interface AttractConditions {
  readonly reducedMotion: boolean
  /**
   * The render tier this device settled on.
   *
   * Attract runs at `low` whatever the device can do, which is deliberate: these are thumbnails
   * beside a page a player is reading, and spending a high-tier frame budget on them would take it
   * from the machine that is actually being played on the front door.
   */
  readonly tier: 'xr' | 'high' | 'medium' | 'low'
  /**
   * Whether the document is visible.
   *
   * A backgrounded tab still runs `requestAnimationFrame` on some platforms and still runs timers
   * on all of them, and a laptop fan spinning up for an arcade nobody is looking at is the kind of
   * thing that gets a web app closed.
   */
  readonly documentVisible: boolean
}

/**
 * How many tiles may animate at once.
 *
 * **Judgement, not measurement**, and it will stay judgement until there is a roster with enough
 * machines on it to measure. Four is chosen as roughly one screenful on a phone: a cap smaller than
 * what fits looks like tiles switching themselves off as you scroll, and a cap larger than what fits
 * is paying for animation nobody sees. The number the cap protects — one shared renderer instead of
 * one per tile — is the part that is not a judgement at all.
 */
export const MAX_LIVE_TILES = 4

/**
 * The tiles that should be animating, in roster order.
 *
 * Roster order rather than "the ones nearest the middle of the screen", because a set that
 * reshuffles as a page scrolls is a set of tiles that start and stop, and a tile that stops is more
 * noticeable than one that never started.
 */
export function liveTiles(
  candidates: readonly AttractCandidate[],
  conditions: AttractConditions,
): readonly string[] {
  // Every one of these is a whole-page answer, so they are asked before anything per-tile is.
  if (conditions.reducedMotion) return []
  if (!conditions.documentVisible) return []
  return candidates
    .filter((tile) => tile.onScreen && tile.hasDefinition)
    .slice(0, MAX_LIVE_TILES)
    .map((tile) => tile.slug)
}

/**
 * The tiles that should be *asking* for their art (M8-79).
 *
 * The same three whole-page answers as `liveTiles`, and then one more: has this tile a picture, and
 * is there anywhere to get one?
 *
 * **`MAX_LIVE_TILES` is deliberately not applied here, and the first version of this applied it.**
 * The reasoning was tidy — a tile that cannot animate should not fetch — and a browser showed what
 * it costs: a tile reserves its canvas box so that the picture can fade in without moving the page,
 * so a tile that never asks is a tile with a permanent empty rectangle above its name. The cap is a
 * limit on **animation**, which is a per-frame cost; a picture is a one-off 1.4 KB that is then
 * cached for ever. A machine hall does not go dark past the fourth cabinet.
 *
 * So every visible tile gets a picture and four of them move. What bounds this is the viewport —
 * `onScreen` comes from an `IntersectionObserver` — and the provider's once-per-hash rule. Scrolling
 * a long roster asks about each machine once, ever, on that browser.
 */
export function tilesToFetch(
  candidates: readonly AttractCandidate[],
  conditions: AttractConditions,
): readonly string[] {
  if (conditions.reducedMotion) return []
  if (!conditions.documentVisible) return []
  return candidates
    .filter((tile) => tile.onScreen && !tile.hasDefinition && tile.fetchable)
    .map((tile) => tile.slug)
}

/**
 * How many frames a second the shared renderer paints, across all live tiles together.
 *
 * **Not per tile.** The renderer walks the live set once per frame, so the cost is this number times
 * the cap however long the roster is. Twelve rather than sixty because a thumbnail's attract is a
 * lamp pattern and a slow marquee, and nothing in it moves fast enough for anybody to see the
 * difference — while the frames it does not spend are frames the machine on the front door keeps.
 */
export const ATTRACT_FPS = 12

/** Milliseconds between shared-renderer frames. */
export const ATTRACT_INTERVAL_MS = 1000 / ATTRACT_FPS

/**
 * Is it worth running the shared renderer at all?
 *
 * Asked so the caller can tear the whole thing down rather than run a loop that paints nothing —
 * a scheduler that keeps ticking over an empty set is a wake-up every 83 ms for no reason, and it
 * is invisible precisely because nothing is drawn.
 */
export function attractIsWorthRunning(live: readonly string[]): boolean {
  return live.length > 0
}
